import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTE_PATH } from '../../constants/routes';
import { useApiErrorTranslation } from '../../hooks/useApiErrorTranslation';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useAuth } from '../../hooks/useAuth';
import { useUser } from '../../hooks/useUser';
import { ErrorNotice } from '@/components/common/ErrorNotice';
import {
  PASSWORD_RESET_PATTERN,
  PASSWORD_RESET_TOKEN_KEY,
} from '@/constants/auth';

import {
  PASSWORD_ALLOWED_CHARS_REGEX,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from '@/constants/validation';

interface ChangePasswordFormData {
  password: string;
  confirmPassword: string;
  agreeAnaTerms: boolean;
}

export const ChangePasswordForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const { translateApiError } = useApiErrorTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const { isSuiteLoungeMember } = useUser();

  const resetToken = searchParams.get(PASSWORD_RESET_TOKEN_KEY);

  const { isRequesting, execute } = useApiRequest(
    'PUT',
    'PasswordResetComplete',
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordFormData>();

  const password = watch('password');
  const agreeAnaTerms = watch('agreeAnaTerms');

  const onFormSubmit = async (data: ChangePasswordFormData) => {
    setErrorMessage('');

    if (!resetToken) {
      setErrorMessage(t('passwordReset.change.errors.invalidToken'));
      return;
    }

    const result = await execute({
      reset_token: resetToken,
      password: data.password,
      agreed_to_terms: data.agreeAnaTerms,
    });

    if (result.ok) {
      const resetPattern = result.data.data.reset_pattern;
      const responseData = result.data.data;
      if (resetPattern === PASSWORD_RESET_PATTERN.INITIAL) {
        // JWTトークンを保存してログイン状態にする
        if (responseData.jwt && responseData.expires_at && responseData.role) {
          auth.login(
            responseData.jwt,
            responseData.expires_at,
            responseData.role,
          );
        }

        navigate(ROUTE_PATH.SIGN_UP_COMPLETE, {
          state: {
            email: responseData.email,
            firstName: responseData.first_name_jp,
            lastName: responseData.family_name_jp,
            password: data.password,
          },
        });
      } else {
        navigate(ROUTE_PATH.PASSWORD_RESET_COMPLETE);
      }
    } else {
      const { message, detailMessages } = translateApiError(
        result.error,
        result.details,
      );
      setErrorMessage(message);
      setErrorDetails(detailMessages);
    }
  };

  const isSubmitEnabled = agreeAnaTerms;

  return (
    <section className="l-card">
      <h2 className="l-card__title">{t('passwordReset.change.title')}</h2>
      <div className="l-card__body">
        <div className="l-text-group l-text-group--white">
          <p className="l-text">{t('passwordReset.change.description')}</p>
        </div>

        {/* 機能コンポーネント：フォーム */}
        <div className="c-auth c-auth--pass">
          <form className="c-auth__form" onSubmit={handleSubmit(onFormSubmit)}>
            <div className="c-auth__fields">
              {/* パスワード */}
              <div className="c-auth__field">
                <label className="c-auth__label">
                  <span className="c-auth__label-text">
                    {t('passwordReset.change.password')}
                  </span>
                  <input
                    className="c-auth__input"
                    type="password"
                    placeholder={t('passwordReset.change.passwordPlaceholder')}
                    {...register('password', {
                      required: t(
                        'passwordReset.change.validation.passwordRequired',
                      ),
                      minLength: {
                        value: PASSWORD_MIN_LENGTH,
                        message: t(
                          'passwordReset.change.validation.passwordMinLength',
                        ),
                      },
                      maxLength: {
                        value: PASSWORD_MAX_LENGTH,
                        message: t(
                          'passwordReset.change.validation.passwordMaxLength',
                        ),
                      },
                      pattern: {
                        value: PASSWORD_ALLOWED_CHARS_REGEX,
                        message: t(
                          'passwordReset.change.validation.passwordInvalidChars',
                        ),
                      },
                    })}
                    required
                  />
                </label>
                {errors.password && (
                  <div className="c-auth__alerts">
                    <p className="c-auth__alert c-auth__alert--error">
                      {errors.password.message}
                    </p>
                  </div>
                )}
              </div>
              {/* パスワード確認 */}
              <div className="c-auth__field">
                <label className="c-auth__label">
                  <span className="c-auth__label-text">
                    {t('passwordReset.change.confirmPassword')}
                  </span>
                  <input
                    className="c-auth__input"
                    type="password"
                    placeholder={t(
                      'passwordReset.change.confirmPasswordPlaceholder',
                    )}
                    {...register('confirmPassword', {
                      required: t(
                        'passwordReset.change.validation.confirmPasswordRequired',
                      ),
                      validate: (value) =>
                        value === password ||
                        t('passwordReset.change.validation.passwordMismatch'),
                    })}
                    required
                  />
                </label>
                {errors.confirmPassword && (
                  <div className="c-auth__alerts">
                    <p className="c-auth__alert c-auth__alert--error">
                      {errors.confirmPassword.message}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* 同意チェック群 */}
            {/* TODO: Figma上に不要と思われるコメントがあるので確認 */}
            <div className="password-change">
              <label className="password-change__confirm">
                <input
                  type="checkbox"
                  className="password-change__confirm-checkbox"
                  {...register('agreeAnaTerms', { required: true })}
                />
                <span className="password-change__confirm-label">
                  {t('passwordReset.change.agreeAnaTermsPrefix')}
                  {isSuiteLoungeMember ? (
                    <a
                      href={t('common.links.loungeReservation.showerAndPrivateRoomTermsOfUse')}
                      target="_blank"
                      className="password-change__link password-change__link--terms-ana"
                      rel="noopener noreferrer"
                    >
                      [{t('passwordReset.change.showerAndPrivateRoomTermsOfUse')}]
                    </a>
                  ) : (
                    <a
                      href={t('common.links.loungeReservation.showerRoomTermsOfUse')}
                      target="_blank"
                      className="password-change__link password-change__link--terms-ana"
                      rel="noopener noreferrer"
                    >
                      [{t('passwordReset.change.showerRoomTermsOfUse')}]
                    </a>
                  )}
                  {t('passwordReset.change.and')}
                  <a
                    href={t('common.links.loungeReservation.privacyPolicy')}
                    target="_blank"
                    className="password-change__link password-change__link--privacy-ana"
                    rel="noopener noreferrer"
                  >
                    [{t('passwordReset.change.privacyPolicy')}]
                  </a>
                  {t('passwordReset.change.agreeAnaTermsSuffix')}
                </span>
              </label>
              {/* <label className="password-change__confirm">
                <input
                  type="checkbox"
                  className="password-change__confirm-checkbox"
                  {...register('agreeWorkhubTerms', { required: true })}
                />
                <span className="password-change__confirm-label">
                  {t('passwordReset.change.agreeWorkhubTermsPrefix')}
                  <a
                    href={t('common.links.workhub.termsOfUse')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="password-change__link password-change__link--terms-workhub"
                  >
                    [{t('passwordReset.change.termsOfUse')}]
                  </a>
                  {t('passwordReset.change.and')}
                  <a
                    href={t('common.links.workhub.privacyPolicy')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="password-change__link password-change__link--privacy-workhub"
                  >
                    [{t('passwordReset.change.privacyPolicy')}]
                  </a>
                  {t('passwordReset.change.agreeWorkhubTermsSuffix')}
                </span>
              </label> */}
              <button
                className={`password-change__submit c-auth__btn c-auth__btn--next ${!isSubmitEnabled || isRequesting ? 'is-disabled' : ''}`}
                type="submit"
                disabled={!isSubmitEnabled || isRequesting}
              >
                {isRequesting
                  ? t('passwordReset.change.submitButtonLoading')
                  : t('passwordReset.change.submitButton')}
              </button>
            </div>
            {errorMessage && (
              <ErrorNotice message={errorMessage} details={errorDetails} />
            )}
          </form>
          <div className="c-auth__links">
            <Link
              to={ROUTE_PATH.PASSWORD_RESET}
              className="c-auth__link c-auth__link--forgot"
            >
              {t('passwordReset.change.backToPasswordReset')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
